import { apiBaseURL } from "./config"
// import * as sentry from "./sentry"
import * as SSR from "../../api/hooks/lib/ssr.js"
import {
    login,
    newNotification,
    bookmarks,
    carts,
    followUpMap,
    lastActivity,
    removeNotification,
    logoutInSeconds,
    logoutAfterInactivitySeconds,
    accessTokenExpireSeconds,
} from "./store"
import { jwtDecode } from "jwt-decode"
import { get } from "svelte/store"
import { _ } from "svelte-i18n"
import { commitTime } from "../../api/hooks/config-client"
import { navigateTo } from "./helper"

// [MIT License](LICENSE.md) © [Jason Miller](https://jasonformat.com/)
const _f = function (
    url: string,
    options: { method?: any; credentials?: any; headers?: any; body?: any }
): Promise<Response | null> {
    if (typeof XMLHttpRequest === "undefined") {
        return Promise.resolve(null)
    }

    options = options || {}
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        const keys: any[] | IterableIterator<string> = []
        const all: IterableIterator<[string, string]> | any[][] = []
        const headers: { [key: string]: string } = {}

        const response = (): Response => ({
            ok: ((request.status / 100) | 0) == 2, // 200-299
            statusText: request.statusText,
            status: request.status,
            url: request.responseURL,
            text: () => Promise.resolve(request.responseText),
            json: () => Promise.resolve(request.responseText).then(JSON.parse),
            blob: () => Promise.resolve(new Blob([request.response])),
            clone: response,
            headers: {
                // @ts-ignore
                keys: () => keys,
                // @ts-ignore
                entries: () => all,
                get: (n) => headers[n.toLowerCase()],
                has: (n) => n.toLowerCase() in headers,
            },
        })

        request.open(options.method || "get", url, true)

        request.onload = () => {
            request
                .getAllResponseHeaders()
                // @ts-ignore
                .replace(/^(.*?):[^\S\n]*([\s\S]*?)$/gm, (m, key, value) => {
                    keys.push((key = key.toLowerCase()))
                    all.push([key, value])
                    headers[key] = headers[key] ? `${headers[key]},${value}` : value
                })
            resolve(response())
        }

        request.onerror = reject

        request.withCredentials = options.credentials == "include"

        for (const i in options.headers) {
            request.setRequestHeader(i, options.headers[i])
        }

        request.send(options.body || null)
    })
}

const _fetch = typeof fetch === "undefined" ? (typeof window === "undefined" ? _f : window.fetch || _f) : fetch
type ApiResponse<T> = {
    data: T
    count: number
}

export const api = async <T>(
    endpoint: string,
    options?: {
        method?: string
        filter?: any
        sort?: string
        limit?: number
        offset?: number
        projection?: string
        headers?: {
            [key: string]: string
        }
        params?: {
            [key: string]: string
        }
        body?: any
        noToken?: boolean
        signal?: AbortSignal
        _groupBy?: string
        showErrors?: boolean
        count?: boolean

        showErrorMessage?: boolean
    }
): Promise<ApiResponse<T>> => {
    if (options && options?.showErrorMessage !== false) options.showErrorMessage = true
    if (typeof window === "undefined") {
        // ssr
        // @ts-ignore
        return context.ssrFetch(endpoint, options)
    }

    // try ssr cache
    const key = SSR.obj2str({ endpoint, options })
    // @ts-ignore
    if (window.__SSR_CACHE__) {
        // @ts-ignore
        const ssrVal = window.__SSR_CACHE__[key]
        console.log("SSR:", key, ssrVal)
        if (ssrVal) {
            return ssrVal
        }
    }

    let method = "GET"
    if (options?.method) method = options.method.toUpperCase()

    let query = options?.count ? "&count=1" : ""
    if (options?.filter) query += "&filter=" + encodeURIComponent(JSON.stringify(options.filter))
    if (options?.sort) query += "&sort=" + options.sort //+ "&sort=_id"
    if (options?.limit) query += "&limit=" + options.limit
    if (options?.offset) query += "&offset=" + options.offset
    if (options?.projection) query += "&projection=" + options.projection
    if (options?._groupBy) query += "&_groupBy=" + options._groupBy

    if (options?.params) {
        Object.keys(options.params).forEach((p) => {
            // @ts-ignore
            query += "&" + p + "=" + encodeURIComponent(options.params[p])
        })
    }

    let headers: any = {
        "Content-Type": "application/json",
    }

    // send jwt
    if (!options?.noToken) {
        try {
            headers["Authorization"] = "Bearer " + (await getLogin())?.tokenString
        } catch (e) {}
    }

    if (options?.headers) headers = { ...headers, ...options.headers }

    let url = apiBaseURL + endpoint + (query ? (endpoint.includes("?") ? query : "?" + query) : "")

    // const span = sentry.startSpan({
    //     op: "fetch",
    //     name: method + " " + url,
    //     // data: Object.assign({}, options, { url }),
    // })

    // will send by sentry automatically
    // const trace_id = span?.toTraceparent()
    // if (trace_id) {
    //     headers["sentry-trace"] = trace_id
    // }

    try {
        const response = await _fetch(url, {
            method,
            // mode: "cors",
            headers,
            body: options?.body ? JSON.stringify(options.body) : null,
            signal: options?.signal,
        })

        // span?.finish()

        // compare release from header
        const _commitTime = parseInt(response?.headers?.get("x-commit-time") || "0")
        if (!isNaN(_commitTime) && _commitTime > commitTime) {
            console.log("new release, reloading...", _commitTime, commitTime)
            newNotification({
                class: "warning",
                html: get(_)("general.newVersionAvailable"),
                timeout: -1,
                removeOnNewLocation: true,
                blockUI: true,
            })
            window.setTimeout(() => {
                window.location.reload()
            }, 1000)
            throw new Error("reload")
        }

        // @ts-ignore
        const contentType = response?.headers.get("Content-Type")
        let data = null
        if (contentType?.includes("application/json")) {
            data = await response?.json()
        }
        if (contentType?.includes("application/pdf")) {
            data = await response?.blob()
        }

        // @ts-ignore
        if (response?.status < 200 || response?.status >= 400) throw { response, data }

        // @ts-ignore
        return { data, count: response?.headers?.get("x-results-count") || 0 }
    } catch (e) {
        if (options?.showErrors && !(e instanceof DOMException)) {
            if (options.showErrorMessage)
                newNotification({
                    class: "error",
                    html: get(_)("general.errorOccurred"),
                    timeout: 6000,
                })

            // @ts-ignore
            throw e.data
        }
        // @ts-ignore
        return false
    }
}

export class LoginError extends Error {
    constructor(
        public message: string,
        public type: "fail" | "mfa",
        public data?: any
    ) {
        super(message)
        this.data = data
    }
}

export const postLogin = async (
    data: { username?: string; password?: string; mfaCode?: string; mfaToken?: string },
    signal?: AbortSignal
): Promise<Login> => {
    const r = await api<ServerLogin>("login", {
        method: "post",
        body: {
            username: data.username,
            password: data.password,
            code: data.mfaCode,
        },
        headers: data.mfaCode ? { Authorization: "Bearer " + data.mfaToken } : undefined,
        noToken: true,
        signal,
    })

    if (r.data?.token) {
        const _l: Login = {
            created: new Date(r.data.created),
            tokenString: r.data.token,
            tokenData: jwtDecode<JWTLoginClaims>(r.data.token),
            account: r.data.account,
        }
        login.set(_l)
        accessTokenExpire = new Date((_l?.tokenData?.exp || 0) * 1000)
        return _l
    }

    // @ts-ignore treat as mfa data
    const mfaData: {
        mfaRequired: boolean
        mfaToken: string
    } = r.data
    if (mfaData?.mfaToken) {
        throw new LoginError("mfa required", "mfa", mfaData)
    }

    throw new LoginError("token not found", "fail")
}

let accessTokenExpire: Date
export const postLogout = async (): Promise<true> => {
    await api("login?logout=1", {
        method: "post",
        body: {},
        noToken: true,
    })

    navigateTo("/", { replace: true })
    login.set(null)

    return true
}
login.subscribe((l) => {
    if (!l) {
        accessTokenExpire = null
    }
})

let refreshingToken: Promise<Login> | null
export const getLogin = async (signal?: AbortSignal): Promise<Login | null> => {
    let _l = get(login)

    accessTokenExpireSeconds.set(_l?.tokenData?.exp - new Date().getTime() / 1000)

    if ((_l?.tokenData?.exp || 0) - 60 < new Date().getTime() / 1000) {
        console.log("Login expired")
        // token expired, relogin via refresh cookie, run only one in parallel
        if (!refreshingToken) refreshingToken = postLogin({}, signal)

        try {
            _l = await refreshingToken
        } finally {
            refreshingToken = null
        }
    }

    return _l
}

// check login every 10 sec, but logout after 4h inactivity
let inactivityNotification: NotificationEntry | null = null
if (typeof window !== "undefined") {
    window.setInterval(() => {
        if (accessTokenExpire) {
            const inactivitySeconds = Math.round((new Date().getTime() - get(lastActivity).getTime()) / 1000)
            const _logoutInSeconds = logoutAfterInactivitySeconds - inactivitySeconds
            logoutInSeconds.set(_logoutInSeconds)

            // notify user 10 min before logout
            if (_logoutInSeconds < 600) {
                const logoutInMinutes = Math.round(_logoutInSeconds / 60)
                if (inactivityNotification) removeNotification(inactivityNotification)
                inactivityNotification = newNotification({
                    class: "warning",
                    html: get(_)("general.logoutWarning", {
                        values: {
                            logoutInMinutes: logoutInMinutes,
                            plural: logoutInMinutes > 1 ? "n" : "",
                        },
                    }),
                    timeout: 9000,
                    removeOnNewLocation: true,
                })
            }

            if (inactivitySeconds > logoutAfterInactivitySeconds) {
                postLogout()
            } else {
                getLogin()
            }
        }
    }, 10000 /* 10 sec */)
}

// init login state
getLogin()
    .then((l) => {
        reloadBookmarks()
    })
    .catch((e) => {
        console.log("getting login via refresh cookie failed")
    })

export const getAccount = async (
    filter: any
): Promise<{
    data: Account[]
    count?: number
}> => {
    return await api<Account[]>("account", {
        filter,
        showErrors: true,
    })
}

export const getDealer = async (
    filter: any,
    limit?: number,
    offset?: number,
    projection?: string,
    sort?: string,
    econfig?: number
): Promise<{
    data: Dealer[]
    count?: number
}> => {
    return await api<Dealer[]>("dealer", {
        filter,
        sort: sort || "number",
        limit,
        offset,
        projection,
        showErrors: true,
        params: econfig ? { econfig: econfig.toString() } : {},
    })
}

export const putAccountChangeRequest = async (dealer: Dealer, account: Account): Promise<true | null> => {
    delete account.changeRequest
    const r = await api("account/" + account.id, {
        method: "put",
        body: {
            changeRequest: {
                dealer,
                account,
            },
        },
        showErrors: true,
    })
    return null
}

export const putAccountPassword = async (
    accountId: string | null,
    newPassword: string,
    setToken?: string
): Promise<true | null> => {
    if (!accountId) {
        const t = jwtDecode<JWTPwResetClaims>(setToken as string)
        accountId = t.id
    }

    const r = await api("account/" + accountId, {
        method: "put",
        body: {
            password: newPassword,
        },
        headers: setToken
            ? {
                  Authorization: "Bearer " + setToken,
              }
            : undefined,
        noToken: !!setToken,
        showErrors: true,
    })
    return null
}

export const postMail = async (
    type:
        | "requestForSubAccount"
        | "requestForAccount"
        | "pwReset"
        | "inquiry"
        | "requestForAssemblyInstructions"
        | "sendPublicOperationLink"
        | "sendOffer"
        | "sendOrderConfirmation",

    data: any,
    noToken?: boolean
) => {
    await api("mail?type=" + type, {
        method: "post",
        body: data,
        noToken,
        showErrors: true,
    })
}
export const postAction = async (
    actionType:
        | "createOrderConfirmationPDF"
        | "createOfferPDF"
        | "setOperationCustomDealerStatus"
        | "setOperationOutCustomDealerStatus"
        | "setOperationCustomInternalStatus"
        | "setOperationOutCustomInternalStatus"
        | "getOperationShopLink",

    data: any,
    noToken?: boolean
): Promise<ApiResponse<any>> => {
    return await api("action", {
        method: "post",
        body: { actionType, ...data },
        noToken,
        showErrors: true,
        showErrorMessage: false,
    })
}
export const getOperation = async <T extends Operation[] | OperationOut[] = Operation[] | OperationOut[]>(
    collectionName: string,
    filter?: any,
    limit?: number,
    offset?: number,
    projection?: string,
    sort?: string,
    signal?: AbortSignal
): Promise<ApiResponse<T>> => {
    const response = await api<T>(collectionName, {
        filter,
        limit,
        offset,
        projection: projection || "list",
        sort,
        signal,
        showErrors: true,
    })
    if (response.data instanceof Array) {
        response.data.map((operation) => {
            operation.collectionName = collectionName == "operation-out" ? "operation-out" : "operation"
        })
    }

    return response
}

export const getOperationGrouped = async (
    _groupBy: string,
    filter?: any,
    limit?: number,
    offset?: number,
    sort?: string,
    signal?: AbortSignal
) => {
    return await api<{ v: string; c: number }[]>("operation", {
        _groupBy,
        filter,
        limit,
        offset,
        projection: "full",
        sort,
        signal,
        showErrors: true,
    })
}

export const getOneOperation = async (id: string, collectionName?: string, o?: { [key: string]: any }) => {
    const collection = collectionName == "operation-out" ? "operation-out" : "operation"

    const operation = await api<OperationOut | Operation>(`${collection}/` + id, {
        projection: "full",
        showErrors: true,
        ...o,
    })

    operation.data.collectionName = collection

    return operation.data
}

export const updateOperationOut = async (operation: OperationOut) => {
    const response = await api<Operation>(`operation-out/` + operation.id, {
        method: "put",
        body: operation,
        showErrors: true,
    })
    return response.data
}

export const createOperationOut = async (operation: OperationOut) => {
    // Der Hook operation-out/post_return.js antwortet mit
    // `{ data: <angelegtes Dokument>, log, status }` – nicht mit dem Dokument
    // selbst. Hier wird das einmal zentral entpackt, damit der Rückgabetyp
    // stimmt und die Aufrufer nicht wissen müssen, wie der Hook verpackt.
    const response = await api<{ data: OperationOut }>("operation-out", {
        method: "post",
        body: operation,
        showErrors: true,
        showErrorMessage: true,
    })

    return response.data?.data ?? (response.data as unknown as OperationOut)
}

export const getProtocol = async (id: string, collection: string) => {
    const response = await api<Protocol[]>("protocol", {
        method: "get",
        filter: {
            collection,
            foreign: id,
        },
    })
    return response.data
}

export const getAttachement = async (opNumber: number | string, accessToken?: string) => {
    const params: any = {
        op: opNumber.toString(),
    }
    if (accessToken) {
        params.accessToken = accessToken
    }

    params.randomAntiCache = Math.random().toString()

    return await api<Attachement[]>("attachement", {
        params,
    })
}

export const getContent = async (path: string): Promise<Content | null> => {
    const c = await api<Content[]>("content", {
        limit: 1,
        filter: { path },
        showErrors: true,
    })
    if (c?.data?.length) {
        return c.data[0]
    }
    return null
}

// Offer Template
export const getActiveOfferTemplate = async (dealerNumber: number, type = "offer", source: number | null) => {
    return api<DocumentTemplate[]>(`document-template`, {
        offset: 0,
        limit: 1,
        filter: {
            active: true,
            type: type,
            source: source,
            dealerNumber: dealerNumber,
        },
        showErrors: true,
        sort: "-updateTime",
    })
}

export const getTemplates = async (type = "offer") => {
    return api<DocumentTemplate[]>(`document-template`, {
        offset: 0,
        filter: {
            type: type,
        },
        showErrors: true,
        sort: "-dealerNumber",
    })
}

export const createOfferTemplate = async (data: DocumentTemplate) => {
    if (data.id) {
        delete data.id
    }
    return api<DocumentTemplate>("document-template", {
        method: "post",
        body: data,
    })
}

export const updateOfferTemplate = async (data: DocumentTemplate, type = "offer") => {
    return api<DocumentTemplate>(`document-template/${data.id}`, {
        method: "put",
        body: data,
        filter: {
            type: type,
            dealerNumber: data.dealerNumber,
        },
        showErrors: true,
    })
}

export const geteOfferTemplatePreview = async (data: DocumentTemplate) => {
    return api<any>(`document-template/${data.id}?preview=pdf`, {
        headers: {
            "Content-Type": "application/json",
            Accept: "application/pdf",
        },
        method: "get",
        limit: 1,
        filter: {
            dealerNumber: data.dealerNumber,
        },
        showErrors: true,
    })
}

// PriceCalculation

export const getPriceCalculationByDealers = async (dealerNumbers: number[] | number) => {
    let loginStore = get(login)
    return api<PriceCalculation[]>(
        `price-calculation${
            // @ts-ignore
            Array.isArray(dealerNumbers) && !loginStore?.tokenData?.roles?.includes("A")
                ? "?subDealers=true"
                : "?subDealers=false"
        }`,
        {
            method: "get",
            offset: 0,
            filter:
                // @ts-ignore
                Array.isArray(dealerNumbers) && !loginStore?.tokenData?.roles?.includes("A")
                    ? { dealerNumber: { $in: dealerNumbers } }
                    : {
                          dealerNumber: Array.isArray(dealerNumbers)
                              ? loginStore?.account?.ident?.dealerNumber
                              : dealerNumbers,
                      },
            showErrors: true,
            sort: "insertTime",
        }
    )
}

export const getPriceCalculations = async (filter: {
    [key: string]: any
}): Promise<{ data: PriceCalculation[]; count: Number }> => {
    return await api<PriceCalculation[]>("price-calculation", { filter })
}

export const getPriceCalculationById = async (Id: string, showErrors = true) => {
    return api<PriceCalculation>(`price-calculation/${Id}?subDealers=true`, {
        method: "get",
        offset: 0,
        showErrors: false,
    })
}
export const createPriceCalculation = async (data: PriceCalculation) => {
    return api<PriceCalculation>("price-calculation", {
        method: "post",
        body: data,
    })
}

export const updatePriceCalculation = async (data: PriceCalculation) => {
    return api<PriceCalculation>(`price-calculation/${data.id}`, {
        method: "put",
        body: data,
        filter: {
            dealerNumber: data.dealerNumber,
        },
        showErrors: true,
    })
}

export const deletePriceCalculation = async (data: PriceCalculation) => {
    return api<PriceCalculation>(`price-calculation/${data.id}`, {
        method: "delete",
        showErrors: true,
    })
}
// Tokens

export const getToken = async (operationId: string, collectionName: string) => {
    return api<AccessToken[]>(`access-token`, {
        method: "get",
        offset: 0,
        limit: 1,
        filter: {
            "permissions.method": "GET",
            "permissions.collection": collectionName,
            "permissions.id": operationId,
        },
        showErrors: true,
    })
}

export const updateToken = async (token: AccessToken, permission: AccessTokenPermission) => {
    return api<AccessToken>(`access-token/${token.id}`, {
        method: "put",
        body: token,
        filter: permission,
        showErrors: true,
    })
}

export const deleteToken = async (token: AccessToken) => {
    return api<AccessToken>(`access-token/${token.id}`, {
        method: "delete",
        showErrors: true,
    })
}

export const createToken = async (token: AccessToken) => {
    return api<AccessToken>("access-token", {
        method: "post",
        body: token,
    })
}

export const getBookmark = async (
    filter: any
): Promise<{
    data: Bookmark[]
    count?: number
}> => {
    return await api<Bookmark[]>("bookmark", {
        filter,
        showErrors: true,
    })
}

export const postBookmark = async (bookmark: Bookmark, method?: string) => {
    return api<Bookmark>("bookmark" + (method == "post" || !method ? "" : "/" + bookmark.id), {
        method: method || "post",
        body: method == "delete" ? undefined : bookmark,
        showErrors: true,
    })
}
export const putBookmark = async (bookmark: Bookmark) =>
    api<Bookmark>("bookmark/" + bookmark.id, {
        method: "put",
        body: bookmark,
        showErrors: true,
    })

export const deleteBookmark = async (bookmark: Bookmark) =>
    api<Bookmark>("bookmark/" + bookmark.id, {
        method: "delete",
        showErrors: true,
    })

export const reloadBookmarks = async () => {
    const _login = get(login),
        personNumber = _login.account.ident.personNumber

    const res = await getAccount({
        "ident.dealerNumber": _login.account.ident.dealerNumber,
    })
    const accounts = res.data,
        _bookmarks: BookmarksMap = {
            operation: {
                [personNumber]: {},
            },
            "operation-out": {
                [personNumber]: {},
            },
            personNumbers: accounts.map((a) => a.ident.personNumber),
        },
        _ident = _login.account.ident
    const { data } = await getBookmark({
        "ident.dealerNumber": _ident?.dealerNumber,
        "ident.personNumber": { $in: _bookmarks.personNumbers },
    })
    data.forEach((b) => {
        if (!_bookmarks[b.type][b.ident.personNumber]) _bookmarks[b.type][b.ident.personNumber] = {}
        _bookmarks[b.type][b.ident.personNumber][b.dataId] = b
    })
    await reloadFollowUp(_bookmarks.personNumbers).then(() => bookmarks.set(_bookmarks))
}

export const addBookmark = async (type: "operation" | "operation-out", id: string) => {
    let bookmark: Bookmark = {
        ident: get(login).account.ident,
        type,
        dataId: id,
    }

    const { data } = await postBookmark(bookmark)
    bookmark = data
    const bMap = get(bookmarks),
        personNumber = bookmark.ident.personNumber
    if (!bMap[type]) bMap[type] = {}
    if (!bMap[type][personNumber]) bMap[type][personNumber] = {}
    bMap[type][personNumber][id] = bookmark
    bookmarks.set(bMap)
    newNotification({ class: "success", html: get(_)("bookmarks.addedBookmarks") })
}

export const removeBookmark = async (type: "operation" | "operation-out", id: string) => {
    const bMap = get(bookmarks),
        personNumber = get(login).account.ident.personNumber,
        bookmark = bMap[type][personNumber][id]
    if (bookmark) await deleteBookmark(bookmark)
    delete bMap[type][personNumber][id]
    bookmarks.set(bMap)
    newNotification({ class: "success", html: get(_)("bookmarks.removedBookmarks") })
}

export const toggleBookmark = async (type: "operation" | "operation-out", id: string) => {
    const bMap = get(bookmarks),
        personNumber = get(login).account.ident.personNumber
    if (bMap?.[type]?.[personNumber]?.[id]) await removeBookmark(type, id)
    else await addBookmark(type, id)
}

// Adress

export const getAddresses = async (
    filter?: any,
    limit?: number,
    offset?: number,
    projection?: string,
    sort?: string,
    subDealers?: boolean,
    query: string = "",
    fillClerks: boolean = false
): Promise<{
    data: Address[]
    count?: number
}> => {
    const addresses = await api<Address[]>(
        "address" + (subDealers ? "?subDealers=true" : "?subDealers=false") + "&" + query,
        {
            filter,
            limit,
            offset,
            projection,
            sort,
            showErrors: true,
        }
    )
    if (fillClerks) {
        const companies = addresses.data.filter((a) => a.type == "company")
        if (companies.length) {
            const clerks = await api<Address[]>(
                "address" + (subDealers ? "?subDealers=true" : "?subDealers=false") + "&" + query,
                {
                    filter: {
                        type: "person",
                        "person.parentCompany": { $in: companies.map((c) => c.id) },
                    },
                    projection,
                    sort,
                    showErrors: true,
                }
            )
            // remove addresses that are already in the clerks list
            addresses.data = addresses.data.filter((a) => !clerks.data.find((c) => c.id == a.id))

            const _addresses: Address[] = []
            addresses.data.forEach((c) => {
                _addresses.push(c)
                clerks.data.forEach((p) => {
                    if (p.person?.parentCompany == c.id) {
                        _addresses.push(p)
                    }
                })
            })

            addresses.data = _addresses
            addresses.count = addresses.data.length
        }
    }

    return addresses
}

export const getAddress = async (id: string) => {
    return await api<Address>(`address/${id}?subDealers=true`, {
        showErrors: false,
    })
}

export const deleteAddress = async (address: Address) => {
    return api<Address>(`address/${address.id}`, {
        method: "delete",
        showErrors: true,
    })
}

export const updateAddress = async (address: Address) => {
    delete address.updateTime

    return await api("address/" + address.id, {
        method: "put",
        body: address,
        showErrors: true,
    })
}

export const createAddress = async (address: Address) => {
    delete address.id

    if (address.type === "person") delete address.company
    else if (address.type === "company") delete address.person

    return api<Address>("address", {
        method: "post",
        body: address,
    })
}

// Carts

export const getCarts = async (filter?: any, limit?: number, offset?: number, sort?: string) => {
    const response = await api<Cart[]>("cart", {
        filter,
        limit,
        offset,
        sort,
        showErrors: true,
    })

    carts.set(response.data)

    return response
}

export const createCart = async (cart: Cart) => {
    return api<Cart>(`cart`, {
        method: "post",
        body: cart,
        showErrors: true,
    })
}

export const updateCart = async (cart: Cart) => {
    return api<Cart>(`cart/${cart.id}`, {
        method: "put",
        body: cart,
        showErrors: true,
    })
}

export const deleteCart = async (cart: Cart) => {
    return api<Cart>(`cart/${cart.id}`, {
        method: "delete",
        showErrors: true,
    })
}

// Order

export const createOrder = async (cart: Cart) => {
    return api<Cart>(`order`, {
        method: "post",
        body: cart,
        showErrors: true,
    })
}

// follow up
export const getFollowUp = async (filter: any) => {
    const followUp = await api<FollowUp[]>("follow-up", {
        filter,
        showErrors: true,
    })
    if (followUp?.data?.length) return followUp.data
    return null
}

export const postFollowUp = async (followUp: FollowUp) =>
    api<FollowUp>("follow-up", {
        method: "post",
        body: followUp,
        showErrors: true,
    })

export const putFollowUp = async (followUp: FollowUp) =>
    api<FollowUp>("follow-up/" + followUp.id, {
        method: "put",
        body: followUp,
        showErrors: true,
    })

export const deleteFollowUp = async (followUp: FollowUp) =>
    api<FollowUp>("follow-up/" + followUp.id, {
        method: "delete",
        showErrors: true,
    })

export const checkIfFollowUpEntriesOverdue = (followUpMap: FollowUpMap) => {
    const overdueEntries = []
    let collectionName: "operation" | "operation-out"
    for (collectionName of ["operation", "operation-out"] as const) {
        for (const personNumber of followUpMap.personNumbers) {
            for (const id in followUpMap[collectionName][personNumber]) {
                const entry = followUpMap[collectionName][personNumber][id]
                if (new Date() > new Date(entry.date)) {
                    overdueEntries.push(entry)
                }
            }
        }
    }
    return !!overdueEntries.length
}
type OverdueMessageValues = {
    total: number
    ownCount: number
    foreignCount: number
    plural: string
}
function getOverdueFollowUpMessage(values: OverdueMessageValues, canSeeForeignFollowUps: boolean): string {
    if (canSeeForeignFollowUps) {
        return get(_)("followUp.ownAndForeign", {
            values: {
                total: values.total,
                plural: values.total === 1 ? "" : "n",
                // Die Zahlen, nicht die Verben. Vorher stand hier
                // `ownCount === 1 ? "ist" : "sind"` – die Vorlage lautet in
                // allen fuenf Sprachen "Davon {ownCount} eigene und
                // {foreignCount} fremde." und erwartet also Zahlen. Heraus
                // kam "Davon sind eigene und sind fremde." (B21). Die
                // Übersetzungen waren richtig, der Aufrufer nicht.
                ownCount: values.ownCount,
                foreignCount: values.foreignCount,
            },
        })
    } else {
        return get(_)("followUp.onlyOwn", {
            values: {
                total: values.total,
                plural: values.total === 1 ? "" : "n",
            },
        })
    }
}
export const reloadFollowUp = async (personNumbers: number[]) => {
    /** @type {Login} */
    const _login = get(login)
    const personNumber = _login.account.ident.personNumber,
        _followUp: FollowUpMap = {
            operation: {
                [personNumber]: {},
            },
            "operation-out": {
                [personNumber]: {},
            },
            personNumbers,
        },
        _ident = _login.account.ident,
        data = await getFollowUp({
            "ident.dealerNumber": _ident?.dealerNumber,
            "ident.personNumber": { $in: personNumbers },
        })
    const overdueEntries = data?.filter((f) => new Date() > new Date(f.date))
    const ownOverdueEntries = overdueEntries?.filter((f) => f.ident.personNumber == personNumber)
    const notOwnOverdueEntries = overdueEntries?.filter((f) => f.ident.personNumber != personNumber)
    if (data) {
        data.forEach((b) => {
            try {
                if (!_followUp[b.type][b.ident.personNumber]) _followUp[b.type][b.ident.personNumber] = {}
                _followUp[b.type][b.ident.personNumber][b.dataId] = b
            } catch (e) {
                console.log(e)
            }
        })
    }

    const loginAt = _login.created,
        loggedInBefore = new Date().getTime() - loginAt.getTime(),
        fiveSeconds = 5000
    const canSeeForeignFollowUps =
        (_login?.account?.roles || [])?.includes("A") || (_login?.account?.permissions || []).includes("P")
    if (canSeeForeignFollowUps) {
        if (loggedInBefore < fiveSeconds && overdueEntries?.length) {
            newNotification({
                class: "warning",
                timeout: 10000,
                html: getOverdueFollowUpMessage(
                    {
                        total: overdueEntries.length,
                        ownCount: ownOverdueEntries.length,
                        foreignCount: notOwnOverdueEntries.length,
                        plural: overdueEntries.length === 1 ? "" : "n",
                    },
                    canSeeForeignFollowUps
                ),
            })
        }
    } else {
        if (loggedInBefore < fiveSeconds && overdueEntries?.length) {
            newNotification({
                class: "warning",
                timeout: 10000,
                html: getOverdueFollowUpMessage(
                    {
                        total: overdueEntries.length,
                        ownCount: 0,
                        foreignCount: 0,
                        plural: overdueEntries.length === 1 ? "" : "n",
                    },
                    false
                ),
            })
        }
    }
    followUpMap.set(_followUp)
}

export const addFollowUp = async (collectionName: "operation" | "operation-out", id: string, date: Date) => {
    const _login = get(login),
        _ident = _login.account.ident
    let _followUp: FollowUp = {
        ident: {
            dealerNumber: _ident?.dealerNumber,
            personNumber: _ident?.personNumber,
        },
        type: collectionName,
        dataId: id,
        date,
    }

    const { data } = await postFollowUp(_followUp)
    _followUp = data

    const personNumber = _followUp.ident.personNumber
    followUpMap.update((followUpMap) => {
        followUpMap[collectionName][personNumber][id] = _followUp
        return followUpMap
    })
    newNotification({ class: "success", html: get(_)("followUp.added") })
}

export const removeFollowUp = async (collectionName: "operation" | "operation-out", id: string) => {
    const fMap = get(followUpMap),
        _login = get(login),
        personNumber = _login.account.ident.personNumber,
        _followUp = fMap[collectionName][personNumber][id]
    if (_followUp) await deleteFollowUp(_followUp)
    delete fMap[collectionName][personNumber][id]
    followUpMap.set(fMap)
    newNotification({ class: "success", html: get(_)("followUp.removed") })
}

// Dealer Status

export async function createAccountToken(description: string): Promise<Partial<AccountToken>> {
    const loginObj = get(login)
    const response = await api("account-token", {
        method: "post",
        body: {
            description,
            ident: loginObj.account.ident,
        },
    })

    return response.data
}

export async function loadAccountTokens(): Promise<AccountToken[]> {
    const response = await api<AccountToken[]>("account-token", { sort: "-insertTime" })
    return response.data
}

export async function deleteAccountToken(id: string): Promise<boolean> {
    await api("account-token/" + id, {
        method: "delete",
    })
    newNotification({
        class: "success",
        html: get(_)("general.accessKeyDeleted"),
    })
    return true
}
